import { createFileRoute } from "@tanstack/react-router";
import { useEffect, useState } from "react";
import { queryOptions, useQuery } from "@tanstack/react-query";

import ContentPage from "@/components/contentPage";
import useApiClient from "@/hooks/useApiClient";
import Toggle from "@/components/toggle";
import { AvailableRoles, AvailableFeatureFlags } from "@/apiClient";
import { hasFeatureFlag, hasRole, titleCase } from "@/utils";
import { toast } from "react-toastify";

const userDetailQueryOptions = (api: any, userId: string) =>
  queryOptions({
    queryKey: ["userDetail", userId],
    queryFn: () => api.users.usersUser({ id: userId }),
    enabled: !!userId,
  });

export const Route = createFileRoute("/user/$userId/")({
  component: UserDetail,
  loader: ({ context: { queryClient, api }, params: { userId } }) =>
    queryClient.prefetchQuery(userDetailQueryOptions(api, userId)),
});

function UserDetail() {
  const api = useApiClient();
  const { userId } = Route.useParams();

  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [isActive, setIsActive] = useState(false);
  const [isVerified, setIsVerified] = useState(false);
  const [isSuperUser, setIsSuperUser] = useState(false);
  const [roles, setRoles] = useState<Record<string, boolean>>({});
  const [featureFlags, setFeatureFlags] = useState<Record<string, boolean>>({});

  const { isPending, data: user } = useQuery(
    userDetailQueryOptions(api, userId),
  );

  useEffect(() => {
    if (!isInitialLoad || user === undefined) return;
    setIsActive(user.is_active);
    setIsVerified(user.is_verified);
    setIsSuperUser(user.is_superuser);

    const roles: Record<string, boolean> = {};
    for (const role of Object.values(AvailableRoles)) {
      roles[role] = hasRole(user, role);
    }
    setRoles(roles);

    const featureFlags: Record<string, boolean> = {};
    for (const flag of Object.values(AvailableFeatureFlags)) {
      featureFlags[flag] = hasFeatureFlag(user, flag);
    }
    setFeatureFlags(featureFlags);
    setIsInitialLoad(false);
  }, [user, isInitialLoad]);

  const makeUpdateUserStatus = (field: string) => async (value: boolean) =>
    api.users.usersPatchUser({
      id: user.id,
      requestBody: { [field]: value },
    });

  const handleUserActiveStatusChange = async (v: boolean) => {
    setIsActive(v);
    try {
      await makeUpdateUserStatus("is_active")(v);
    } catch (error) {
      setIsActive(!v);
      toast.error("Failed to update user active status.");
    }
  };

  const handleUserVerifiedStatusChange = async (v: boolean) => {
    setIsVerified(v);
    try {
      await makeUpdateUserStatus("is_verified")(v);
    } catch (error) {
      setIsVerified(!v);
      toast.error("Failed to update user verified status.");
    }
  };

  const handleUserSuperUserStatusChange = async (v: boolean) => {
    setIsSuperUser(v);
    try {
      await makeUpdateUserStatus("is_superuser")(v);
    } catch (error) {
      setIsSuperUser(!v);
      toast.error("Failed to update super user status.");
    }
  };

  const makeHandleUserFeatureFlagChange = (flag: string) => async (v: boolean) => {
    const updatedFlags = { ...featureFlags, [flag]: v };
    setFeatureFlags(updatedFlags);
    return api.users.setFeatureFlagsForUser({
      userId: user.id,
      requestBody: Object.entries(updatedFlags)
        .filter(([_, isEnabled]) => isEnabled)
        .map(([f, _]) => f) as AvailableFeatureFlags[],
    });
  };

  const makeHandleUserRoleChange = (role: string) => async (v: boolean) => {
    const updatedRoles = { ...roles, [role]: v };
    setRoles(updatedRoles);
    api.users.setRolesForUser({
      userId: user.id,
      requestBody: Object.entries(updatedRoles)
        .filter(([_, isEnabled]) => isEnabled)
        .map(([r, _]) => r) as AvailableRoles[],
    });
  };

  if (!user || isPending) return "Loading...";
  return (
    <ContentPage title={`User Detail: ${user.email}`}>
      <div className={"flex flex-col lg:w-1/3"}>
        <>
          <h1 className={"pb-6 text-2xl text-blue-200"}>Status</h1>
          <div className={"flex flex-col gap-2 p-2"}>
            <Toggle
              value={isActive}
              onChange={handleUserActiveStatusChange}
              label={"Active"}
            />
            <Toggle
              value={isVerified}
              onChange={handleUserVerifiedStatusChange}
              label={"Verified"}
            />
            <Toggle
              value={isSuperUser}
              onChange={handleUserSuperUserStatusChange}
              label={"Super User"}
            />
          </div>
        </>
        <>
          <h1 className={"pb-6 text-2xl text-blue-200"}>Roles</h1>
          <div className="align-end flex flex-col gap-2 p-2">
            {Object.entries(roles).map(([role, hasRole]) => {
              const label = titleCase(role);
              return (
                <Toggle
                  key={role}
                  value={hasRole}
                  onChange={makeHandleUserRoleChange(role)}
                  label={label}
                />
              );
            })}
          </div>
        </>
        <>
          <h1 className={"pb-6 text-2xl text-blue-200"}>Feature Flags</h1>
          <div className="align-end flex flex-col gap-2 p-2">
            {Object.entries(featureFlags).map(([flag, hasRole]) => {
              const label = titleCase(flag);
              return (
                <Toggle
                  key={flag}
                  value={hasRole}
                  onChange={makeHandleUserFeatureFlagChange(flag)}
                  label={label}
                />
              );
            })}
          </div>
        </>
      </div>
    </ContentPage>
  );
}
